import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Upload,
  FolderOpen,
  Eye,
  Users,
  Gift,
  FileText,
  Shield,
  Clock,
  Heart,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/shared/Navbar";
import { getAccessToken } from "../storage/storage";
import Footer from "../components/shared/Footer";
import NavbarWeb from "../components/NavbarWeb";

const About = () => {
  const token = getAccessToken();
  const features = [
    {
      icon: Upload,
      title: "Upload Any Format",
      description: "PDFs, images, Word docs, or zip files",
    },
    {
      icon: FolderOpen,
      title: "Organize Securely",
      description: "Keep all files in one secure place",
    },
    {
      icon: Eye,
      title: "Easy Viewing",
      description: "View summaries and highlights easily",
    },
    {
      icon: Users,
      title: "Share with Doctors",
      description: "Invite doctors to view your documents",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <NavbarWeb />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-green-100 p-2 rounded-xl inline-flex justify-center items-center w-16 h-16 mb-8">
            <img
              src="/assets/logo/logo.svg"
              alt="MedTag Logo"
              className="h-12 w-12"
            />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            About <span className="text-[#175635]">MedTag</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            MedTag is a modern medical documentation platform designed to help
            individuals manage their health records with ease. Whether you're
            navigating a chronic condition, preparing for a consultation, or
            simply organizing past reports, MedTag gives you a centralized and
            reliable space to store and access your medical files.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#EAF8EF]/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why <span className="text-[#175635]">MedTag</span>?
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Medical files are often scattered across devices, emails,
                hospitals, and folders. MedTag eliminates this mess by offering
                a unified platform where patients can upload documents like lab
                reports, prescriptions, scan images, discharge summaries, and
                more.
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#175635] rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Secure & Reliable
                  </h4>
                  <p className="text-gray-600">
                    Your medical data is protected with enterprise-grade
                    security
                  </p>
                </div>
              </div>
            </div>
            <Card className="shadow-lg border-0 bg-white">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <span className="text-gray-600 line-through">
                      Scattered medical files
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <span className="text-gray-600 line-through">
                      Lost prescriptions
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <span className="text-gray-600 line-through">
                      Email attachments mess
                    </span>
                  </div>
                  <div className="border-t pt-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-3 h-3 bg-[#175635] rounded-full"></div>
                      <span className="text-[#175635] font-medium">
                        All files in one secure place
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#EAF8EF] rounded-full mb-6">
              <Heart className="h-8 w-8 text-[#175635]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Share with <span className="text-[#175635]">Doctors</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Patients can share files or summaries with doctors for faster
              understanding, better decisions, and clearer history. It saves
              time for both patients and doctors.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center p-6 border-2 border-[#EAF8EF] hover:border-[#175635] transition-colors duration-300">
              <CardContent className="p-0">
                <Clock className="h-12 w-12 text-[#175635] mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">
                  Faster Consultations
                </h3>
                <p className="text-gray-600">
                  Doctors can review your history before appointments
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-2 border-[#EAF8EF] hover:border-[#175635] transition-colors duration-300">
              <CardContent className="p-0">
                <Eye className="h-12 w-12 text-[#175635] mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">
                  Better Decisions
                </h3>
                <p className="text-gray-600">
                  Complete medical history leads to informed choices
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-2 border-[#EAF8EF] hover:border-[#175635] transition-colors duration-300">
              <CardContent className="p-0">
                <Users className="h-12 w-12 text-[#175635] mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">
                  Clear Communication
                </h3>
                <p className="text-gray-600">
                  Seamless sharing eliminates confusion
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#EAF8EF]/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Simplicity & <span className="text-[#175635]">Features</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to manage your medical documents in one
              easy-to-use platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="text-center p-6 bg-white shadow-lg border-0 hover:shadow-xl transition-shadow duration-300"
              >
                <CardContent className="p-0">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[#EAF8EF] rounded-full mb-4">
                    <feature.icon className="h-7 w-7 text-[#175635]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-gray-600 mb-2">
              Plus membership plans available for enhanced features
            </p>
          </div>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Card className="bg-gradient-to-r from-[#175635] to-[#175635]/90 text-white p-8 shadow-2xl">
            <CardContent className="p-0">
              <Gift className="h-16 w-16 text-white mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4">
                Spread the Word, Get Rewarded
              </h2>
              <p className="text-xl mb-6 text-green-50">
                Bring 3 or more friends and get one month free as a thank-you.
              </p>
              <Link to={token ? "/referral" : "/login?next=/referral"}>
                <Button className="bg-white text-[#175635] hover:bg-gray-100 font-semibold px-8 py-3">
                  Start Referring
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-16 bg-[#EAF8EF]/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
            Take Control of Your{" "}
            <span className="text-[#175635]">Medical Journey</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            MedTag empowers you to take control of your medical journey. No more
            missing reports. Just reliable, clean access to your health
            documents, for you and your doctors.
          </p>

          {/* xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx  MIGHT USE THESE LATER  xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx */}
          {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-[#175635] hover:bg-[#175635]/90 text-white px-8 py-3 text-lg">
              Get Started Today
            </Button>
            <Button
              variant="outline"
              className="border-[#175635] text-[#175635] hover:bg-[#EAF8EF] px-8 py-3 text-lg"
            >
              Learn More
            </Button>
          </div> */}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default About;
